import { asArray, isTrue } from './utils';
import { Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface IGroupsManagerChanges<Token = string> {
  include: Token[];
  exclude: Token[];
}

export class GroupsManager<Group = any, Token = string> {
  public readonly core: Map<Group, Map<number, Token>> = new Map<Group, Map<number, Token>>();

  public readonly hash: Map<Group, Map<number, Token>> = new Map<Group, Map<number, Token>>();

  public readonly wait: Set<Group> = new Set<Group>();

  private readonly emitter$: Subject<void> = new Subject<void>();

  public updated$: Observable<this> = this.emitter$.asObservable().pipe(map(() => this));

  private apply = (): this => {
    this.emitter$.next();
    return this;
  };

  private getUniqueTokens = (tokens: Token | Token[]): Map<number, Token> => {
    const uniqueTokens = new Set<Token>(asArray(tokens));
    const groupData = [];
    const len = uniqueTokens.size + 1;

    asArray<Token>(tokens).forEach(token => {
      if (uniqueTokens.has(token)) {
        groupData.push([len - uniqueTokens.size, token]);
        uniqueTokens.delete(token);
      }
    });

    return new Map<number, Token>(groupData);
  };

  private getSortedTokens = (group: Map<number, Token>) => {
    const indexes = Array.from(group?.keys() || []).sort((a, b) => a - b);
    return indexes.map(i => group?.get(i));
  };

  public create = (groupName: Group, tokens: Token | Token[], createHash: boolean = false): this => {
    const { core, wait, getUniqueTokens, apply, setHash } = this;

    core.set(groupName, getUniqueTokens(tokens));
    wait.delete(groupName);

    return createHash ? setHash(groupName, tokens) : apply();
  };

  public read = (groupName: Group): Token[] => {
    const { core, getSortedTokens } = this;
    return getSortedTokens(core.get(groupName));
  };

  public update = (
    groupName: Group,
    add: Token | Token[] = [],
    remove: Token | Token[] = [],
    emitEvent: boolean = true
  ): this => {
    const { core, read, getUniqueTokens, apply } = this;
    const group = core.get(groupName);

    if (group) {
      const deadTokens: Set<Token> = new Set<Token>(asArray(remove));
      const tokens = getUniqueTokens(
        read(groupName)
          .filter(token => !deadTokens.has(token))
          .concat(asArray(add))
      );

      core.set(groupName, tokens);

      return emitEvent ? apply() : this;
    } else {
      return this.create(groupName, add);
    }
  };

  public replace = (groupName: Group, oldToken: Token, newToken: Token = null): this => {
    return this.create(
      groupName,
      this.read(groupName)
        .map(token => (token === oldToken ? newToken : token))
        .filter(isTrue)
    );
  };

  public delete = (groupName: Group, emitEvent: boolean = true): this => {
    // Deletes named Group
    const groupExists = this.core.has(groupName);

    this.core.delete(groupName);
    this.hash.delete(groupName);

    return groupExists && emitEvent ? this.apply() : this;
  };

  public remove = (token: Token) => {
    // Removes token from all the Groups
    Array.from(this.core.keys()).forEach(groupName => this.replace(groupName, token));
    return this.apply();
  };

  public has = (groupName: Group): boolean => this.core.has(groupName);

  public groupHas = (groupName: Group, data: Token | Token[], some: boolean = true): boolean => {
    const group = this.core.get(groupName);

    if (group) {
      const values = new Set<Token>(Array.from(group.values()));
      const tokensToCheck = asArray(data).map(t => `${t}`);

      const lambda = token => values.has(token);

      return some ? tokensToCheck.some(lambda) : tokensToCheck.every(lambda);
    } else {
      return false;
    }
  };

  public getGroups = (): Group[] => {
    return Array.from(this.core.keys());
  };

  public getTokens = (): Token[] => {
    const { read, getGroups } = this;
    const tokens = [];

    for (const group of getGroups()) {
      for (const token of read(group)) {
        tokens.push(token);
      }
    }

    return tokens;
  };

  public clear = (): this => {
    this.core.clear();
    return this.apply();
  };

  public setHash = (groupName: Group, tokens: Token | Token[]): this => {
    const { hash, getUniqueTokens, apply } = this;
    hash.set(groupName, getUniqueTokens(tokens));
    return apply();
  };

  public hashToValue = (groupName: Group): this => {
    const { create, getSortedTokens, hash } = this;
    return hash.has(groupName) ? create(groupName, getSortedTokens(hash.get(groupName))) : this;
  };

  public valueToHash = (groupName: Group): this => {
    const { setHash, read, core } = this;
    return core.has(groupName) ? setHash(groupName, read(groupName)) : this;
  };

  public readHash = (groupName: Group): Token[] => {
    const { getSortedTokens, hash } = this;
    return getSortedTokens(hash.get(groupName));
  };

  public removeHash = (groupName: Group): this => {
    const { hash } = this;
    hash.delete(groupName);

    return this;
  };

  public detectChanges = (groupName: Group, log: boolean = false): IGroupsManagerChanges<Token> => {
    const { core, hash, getSortedTokens } = this;
    const changes: IGroupsManagerChanges<Token> = {
      include: [],
      exclude: [],
    };
    const coreTokens = core.has(groupName) && new Set<Token>(getSortedTokens(core.get(groupName)));
    const hashTokens = hash.has(groupName) && new Set<Token>(getSortedTokens(hash.get(groupName)));

    log && console.log(coreTokens, hashTokens);

    if (
      !coreTokens ||
      !hashTokens ||
      (coreTokens.size === hashTokens.size && Array.from(coreTokens.values()).every(token => hashTokens.has(token)))
    ) {
      return null;
    } else {
      for (const token of Array.from(coreTokens.values())) {
        !hashTokens.has(token) ? changes.include.push(token) : hashTokens.delete(token);
      }

      for (const token of Array.from(hashTokens.values())) {
        changes.exclude.push(token);
      }
    }

    return [changes.include, changes.exclude].some(c => !!c.length) ? changes : null;
  };

  public toggleWaiting = (groupName: Group): this => {
    const { wait } = this;
    wait.add(groupName);

    return this;
  };

  public isWaiting = (groupName: Group): boolean => {
    const { wait } = this;
    return wait.has(groupName);
  };

  public readAsync = (groupName: Group): Observable<Token[]> => {
    const { updated$, isWaiting, has, read } = this;

    return isWaiting(groupName) || !has(groupName)
      ? updated$.pipe(
          filter(() => !isWaiting(groupName) && has(groupName)),
          map(() => read(groupName))
        )
      : of(read(groupName));
  };
}
